import cx from 'classnames';
import startCase from 'lodash/startCase';
import {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {VendorImages} from '~/CreateFabricInvitation/routes/EventGifting/components/VendorImages/VendorImages';
import {useEventGiftingClassifiedCategory} from '~/CreateFabricInvitation/routes/EventGifting/useEventGiftingClassifiedCategory';
import type {GiftOptionProps} from '~/CreateFabricInvitation/types/types';
import {FEATURE_QA_IDS} from '~/CreateFabricInvitation/utils/constants';
import {scrollElementIntoViewById} from '~/CreateFabricInvitation/utils/misc';
import {InvitationWidget} from '~/common/components/InvitationWidget/InvitationWidget';
import {UPDATE_GIFT_OPTIONS} from '~/common/fabric/reducers/giftingOptions/constants';
import {
  selectRegistryRecipient,
  selectRegistryUrls,
  selectRegistryWording,
} from '~/common/fabric/reducers/giftingOptions/selectors';
import {EventFeature} from '~/common/types/event';
import {useIsLargeAndUp} from '~library/hooks/useMatchQuery';
import {GiftboxIcon} from '~library/svg/GiftboxIcon';
import {RegistryOverlay} from './components/RegistryOverlay';
import {RegistryOverlayContent} from './components/RegistryOverlayContent';

import './Registries.sass';

interface RegistriesProps extends GiftOptionProps {
  inPreviewStep: boolean;
}

export const Registries = ({
  inPreviewStep,
  hideIfInactive = false,
  hideIfActive = false,
  renderAs = 'block',
}: RegistriesProps) => {
  const dispatch = useDispatch();
  const registryUrls = useSelector(selectRegistryUrls);
  const [showOverlay, setShowOverlay] = useState(false);
  const giftRegistryWording = useSelector(selectRegistryWording);
  const registryRecipient = useSelector(selectRegistryRecipient);
  const hasRegistries = registryUrls && registryUrls.length > 0;
  const isLargeAndUp = useIsLargeAndUp();
  const {isWeddingEvent, isBabyEvent, isFuneralEvent} = useEventGiftingClassifiedCategory();

  const closeOverlay = useCallback(() => setShowOverlay(false), []);

  const onClickAddOrEditHandler = useCallback(() => {
    scrollElementIntoViewById('submodule-registry', 'center', 0);
    setShowOverlay(true);
  }, []);

  useEffect(() => {
    dispatch({
      type: UPDATE_GIFT_OPTIONS,
      payload: {
        // biome-ignore lint/style/useNamingConvention: existing error
        force_show_module: showOverlay,
      },
    });

    return () => {
      dispatch({
        type: UPDATE_GIFT_OPTIONS,
        payload: {
          // biome-ignore lint/style/useNamingConvention: existing error
          force_show_module: false,
        },
      });
    };
  }, [dispatch, showOverlay]);

  if (isFuneralEvent) return null;

  let title = 'Wishlist';

  if (isWeddingEvent || isBabyEvent) {
    title = 'Registry or wishlist';
  }

  if (registryRecipient && renderAs === 'block') {
    title = `${startCase(registryRecipient)}'s ${startCase(giftRegistryWording)}`;
  }

  const hideContainer = Boolean(
    (hideIfInactive && !hasRegistries) || (hideIfActive && hasRegistries)
  );

  return (
    <div
      className={cx('event-gifting__registries', {
        'event-gifting__registries__preview-step': inPreviewStep,
        'event-gifting__registries__hide': hideContainer,
      })}
    >
      {inPreviewStep && isLargeAndUp ? null : <VendorImages />}
      <InvitationWidget.Toggle
        title={title}
        subtitle={hasRegistries ? `${registryUrls.length} added` : undefined}
        onClick={onClickAddOrEditHandler}
        filled={hasRegistries}
        startAdornment={<GiftboxIcon ratio={0.5} />}
        qaId={FEATURE_QA_IDS[EventFeature.WISHLIST] as string}
        renderAs={renderAs}
        editable
        hide={hideContainer}
        overlay={
          <RegistryOverlay closeOverlay={closeOverlay} isOpen={showOverlay}>
            <RegistryOverlayContent onClose={closeOverlay} key={Number(showOverlay)} />
          </RegistryOverlay>
        }
      />
    </div>
  );
};
